import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { RouteSatClientsPage } from 'routes'

import {
  useFetchPartnerSatSecretsQuery,
  useCreatePartnerSatSecretMutation,
} from 'api/partnerSatSecret'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Header from 'components/Shared/Header/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import IconLink from 'components/Shared/IconLink/Index'
import { confirm } from 'components/Shared/ConfirmDialog/Index'

import ConfirmDialogBody from './ConfirmDialogBody'
import SatSecretsTable from './SatSecretsTable'
import SecretModal from './SecretModal'

const SatSecretsPage = () => {
  const params = useParams()
  const selectedSatClient = params.satClientId || null

  const { data, isFetching } = useFetchPartnerSatSecretsQuery({ satClientId: selectedSatClient })
  const [createSecret, { isLoading: isCreating }] = useCreatePartnerSatSecretMutation()

  const satSecrets = data?.satSecrets || []
  const satClientID = data?.meta?.clientId
  const isLoading = isFetching || isCreating

  const { t } = useTranslation()

  const [isSecretModalVisible, setIsSecretModalVisible] = useState(false)
  const [secret, setSecret] = useState(null)

  const getBreadcrumbs = () => {
    const partnerUrl = RouteSatClientsPage()
    const breadcrumbsList = [
      {
        text: t('common.breadcrumbSatClients'),
        url: partnerUrl,
      },
      { text: satClientID },
      { text: t('common.breadcrumbSatSecrets') },
    ]

    return breadcrumbsList
  }

  const onCloseSecretModal = () => {
    setIsSecretModalVisible(false)
  }

  const createSecretWithDialog = () => {
    confirm({
      title: t('satSecretsPage.addConfirmDialogTitle'),
      cancelLabel: t('common.cancel'),
      showJustification: true,
      bodyComponent: ConfirmDialogBody,
      bodyComponentProps: { message: t('satSecretsPage.addConfirmDialogMsg') },
      t,
    }).then(
      (justification) => { onCreateSecret(justification) },
      () => {}
    )
  }

  const onCreateSecret = async ({ justification }) => {
    const data = await createSecret({
      satClientId: selectedSatClient,
      justification: justification,
    }).unwrap()

    if (data.error) {
      return
    }

    setSecret(data?.secret)
    setIsSecretModalVisible(true)
  }

  return (
    <MainWrap pageTitle={t('satSecretsPage.pageHeader')}>
      <Content loading={isLoading}>
        <SecretModal
          visible={isSecretModalVisible}
          onClose={onCloseSecretModal}
          secret={secret}
        />

        <Header
          title={t('satSecretsPage.pageHeader')}
          breadcrumbsList={getBreadcrumbs()}
        />

        <ActionButtons>
          <IconLink
            iconName='icon-add-big-24'
            text={t('satSecretsPage.addSecret')}
            onClick={() => createSecretWithDialog()}
            disabled={!selectedSatClient}
          />
        </ActionButtons>

        <SatSecretsTable satSecrets={satSecrets} />
      </Content>
    </MainWrap>
  )
}

export default SatSecretsPage
